// Portfolio Administration v1.0 PR 5 — Local Enforcers registry table.

import { useNavigate } from "react-router-dom";
import {
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import type { LocalEnforcerDto } from "@/api/portfolioAdmin/pr4bTypes";
import { RegistryStatusPill } from "@/components/portfolioAdmin/registry/RegistryStatusPill";
import { RatingStars } from "@/components/portfolioAdmin/registry/RatingStars";
import { useDateFormatter } from "@/utils/datetime";

const TYPE_LABELS: Record<string, string> = {
  "law-firm": "Law firm",
  "collection-agency": "Collection agency",
  "receiver-trustee": "Receiver / trustee",
  "process-server": "Process server",
  auctioneer: "Auctioneer",
  liquidator: "Liquidator",
  "appraiser-for-recovery": "Appraiser",
  locksmith: "Locksmith",
  "security-firm": "Security firm",
  "repossession-firm": "Repossession firm",
  "skip-tracer": "Skip-tracer",
  investigator: "Investigator",
};

export interface LocalEnforcersListTableProps {
  enforcers: LocalEnforcerDto[];
}

export function LocalEnforcersListTable({
  enforcers,
}: LocalEnforcersListTableProps) {
  const navigate = useNavigate();
  const { date } = useDateFormatter();
  if (enforcers.length === 0) {
    return (
      <div
        className="text-small text-gray-mid italic px-space-3 py-space-4 text-center border border-border-light rounded-lg bg-white-pure"
        data-testid="enforcers-empty-state"
      >
        No local enforcers match these filters yet.
      </div>
    );
  }
  return (
    <Table data-testid="enforcers-list-table">
      <THead>
        <TR>
          <TH className="text-white-pure">Name</TH>
          <TH className="text-white-pure">Type</TH>
          <TH className="text-white-pure">Status</TH>
          <TH className="text-white-pure">Onboarded</TH>
          <TH className="text-white-pure">Engagements</TH>
          <TH className="text-white-pure">Rating</TH>
        </TR>
      </THead>
      <TBody>
        {enforcers.map((e) => (
          <TR
            key={e.id}
            className="cursor-pointer"
            data-testid={`enforcers-row-${e.id}`}
            onClick={() => navigate(`/portfolio-admin/local-enforcers/${e.id}`)}
          >
            <TD>
              <div className="flex flex-col">
                <span className="text-gold-shadow hover:underline font-semibold">
                  {e.legalEntityName}
                </span>
                {e.dbaName ? (
                  <span className="text-small text-gray-mid">
                    DBA {e.dbaName}
                  </span>
                ) : null}
              </div>
            </TD>
            <TD>{TYPE_LABELS[e.enforcerType] ?? e.enforcerType}</TD>
            <TD>
              <RegistryStatusPill status={e.status} />
            </TD>
            <TD className="text-small text-gray-mid">
              {e.onboardedDate ? date(e.onboardedDate) : "—"}
            </TD>
            <TD className="text-gray-mid">—</TD>
            <TD>
              <RatingStars rating={e.overallRating} />
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}
